import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BarItem, BarItemProps } from "@nivo/bar";
import { animated, to } from "@react-spring/web";
import { useInView } from "react-intersection-observer";
import { buildClasses } from "../utils/buildClasses";
import { ScrollContainerContext } from "../rye/RyeMainContentContainerBase";
import { RyeChartTooltip } from "../rye/RyeChartTooltip";
import { DollarAmount, useFormatDollarAmount } from "../common/DollarAmount";
import { MerchantLogo } from "../common/MerchantLogo";
import {
  GroupedBarChart,
  groupedBarChartHeightPx,
  GroupedBarDatum,
  useGroupedBars,
} from "../common/GroupedBarChart";
import type { GroupedBarGroup, GroupedBarRow } from "../common/groupedBarRows";
import type { BreakdownSelection } from "./breakdownChartParts";
import type {
  MerchantSort,
  MerchantTotal,
  MerchantType,
  TopMerchantsData,
} from "./useTopMerchantsData";

// Hex values matching the Tailwind tokens in tailwind.config.js; Nivo needs
// real colors, not class names.
const COLORS: Record<MerchantType, string> = {
  expenses: "#0ea5e9", // blue (sky) 500
  income: "#3FD088", // green 500
};
const NUMBER_TEXT_COLOR = "#475569"; // on-surface-600

const COLUMN_WIDTH_PX = 108;
const COLUMN_GAP_PX = 16;
const COLUMNS = ["Total", "Transactions", "Average"] as const;
const MARGIN_RIGHT_PX = COLUMN_GAP_PX + COLUMNS.length * COLUMN_WIDTH_PX;

// Merchants are shown this many at a time, with more added as the end of the
// list scrolls near.
const PAGE_SIZE = 25;
// How far ahead of the end of the list to add more, so they're usually there
// before they're scrolled to.
const LOAD_AHEAD_PX = 400;

/** One merchant's row. Every row stands alone: none of them expand. */
interface MerchantBarRow extends GroupedBarRow, MerchantTotal {
  type: MerchantType;
  currency: string;
}

/** Nivo's own bar, then the row's numbers in fixed columns at the right. */
function MerchantBar(props: BarItemProps<GroupedBarDatum>): JSX.Element {
  const { allRowsById, innerWidth } = useGroupedBars<MerchantBarRow>();
  const formatDollarAmount = useFormatDollarAmount();
  const row = allRowsById.get(String(props.bar.data.indexValue));
  const formatWhole = (amount: number) =>
    formatDollarAmount(amount, {
      currencyCode: row?.currency,
      showDollarSign: true,
      showMinus: true,
    });
  const { transform, height, labelOpacity } = props.style;
  const columnX = (index: number) =>
    innerWidth + COLUMN_GAP_PX + (index + 1) * COLUMN_WIDTH_PX;
  return (
    <>
      <BarItem {...props} />
      {row && (
        <animated.g
          transform={transform}
          opacity={labelOpacity}
          style={{
            pointerEvents: "none",
            fontFamily: "InterVariable, sans-serif",
            fontSize: 12,
            fill: NUMBER_TEXT_COLOR,
          }}
        >
          {[
            formatWhole(row.total),
            row.count.toLocaleString(),
            formatWhole(row.total / row.count),
          ].map((text, index) => (
            <animated.text
              key={COLUMNS[index]}
              x={columnX(index)}
              y={to(height, (h) => h / 2)}
              textAnchor="end"
              dominantBaseline="central"
            >
              {text}
            </animated.text>
          ))}
        </animated.g>
      )}
    </>
  );
}

function MerchantLabelLogo({ row }: { row: MerchantBarRow }): JSX.Element {
  return (
    <span className={buildClasses("shrink-0", "mr-1.5")}>
      <MerchantLogo merchant={row.merchant} size={18} />
    </span>
  );
}

function MerchantTooltip({ row }: { row: MerchantBarRow }): JSX.Element {
  const amount = (n: number) => (
    <DollarAmount
      n={n}
      size="sm"
      showDollarSign={true}
      align="right"
      currencyCode={row.currency}
    />
  );
  const date = (d: MerchantTotal["firstDate"]) => (
    <span className={buildClasses("text-right")}>
      {d.format("MMM D, YYYY")}
    </span>
  );
  const label = (text: string) => (
    <span className={buildClasses("text-on-surface-500")}>{text}</span>
  );
  return (
    <RyeChartTooltip>
      <div
        className={buildClasses(
          "flex",
          "flex-col",
          "gap-1.5",
          "bg-surface",
          "border",
          "border-surface-600",
          "shadow-glow",
          "rounded-md",
          "px-3",
          "py-2",
          "text-sm",
          "text-on-surface-700",
        )}
      >
        <span className={buildClasses("font-medium")}>{row.label}</span>
        <div
          className={buildClasses(
            "grid",
            "grid-cols-[auto_auto]",
            "gap-x-4",
            "gap-y-0.5",
          )}
        >
          {label("Total")}
          {amount(row.total)}
          {label("Transactions")}
          <span className={buildClasses("text-right")}>
            {row.count.toLocaleString()}
          </span>
          {label("Average")}
          {amount(row.total / row.count)}
          {row.count === 1 ? (
            <>
              {label("Date")}
              {date(row.firstDate)}
            </>
          ) : (
            <>
              {label("First")}
              {date(row.firstDate)}
              {label("Last")}
              {date(row.lastDate)}
            </>
          )}
        </div>
      </div>
    </RyeChartTooltip>
  );
}

/** The column headings, which stay in view as the list scrolls. */
function ColumnHeadings(): JSX.Element {
  return (
    <div
      className={buildClasses(
        "sticky",
        "top-0",
        "z-10",
        "flex",
        "justify-end",
        "pb-1",
        "bg-surface",
      )}
    >
      <div
        className={buildClasses(
          "flex",
          "text-xs",
          "font-medium",
          "text-on-surface-500",
        )}
      >
        {COLUMNS.map((column) => (
          <span
            key={column}
            className={buildClasses("text-right")}
            style={{ width: `${COLUMN_WIDTH_PX}px` }}
          >
            {column}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Merchants as ranked bars, with each one's total, number of transactions, and
 * average in columns at the right. The bars show whatever the merchants are
 * ranked by, so the first is always the longest, and adding more below never
 * changes the scale. That's also why there's no value axis: the columns give
 * the numbers, and an axis at the end of a list this long would be out of
 * sight.
 *
 * Merchants are added a page at a time as the end of the list scrolls into
 * view. A new question — another type, ranking, or date range — starts back
 * at the first page, at the top.
 */
export function TopMerchantsBars({
  data,
  type,
  sort,
  questionKey,
  selectedId,
  onSelect,
}: {
  data: TopMerchantsData;
  type: MerchantType;
  sort: MerchantSort;
  /** Changes whenever the merchants are asked for differently. */
  questionKey: string;
  selectedId: string | null;
  onSelect: (selection: BreakdownSelection) => void;
}): JSX.Element {
  const { merchants, currency } = data;
  const scrollContainer = useContext(ScrollContainerContext);

  // Reset as the question changes, during the same render, so the new
  // ranking never draws a frame with the old one's longer list.
  const [shownCount, setShownCount] = useState(PAGE_SIZE);
  const [shownQuestionKey, setShownQuestionKey] = useState(questionKey);
  if (shownQuestionKey !== questionKey) {
    setShownQuestionKey(questionKey);
    setShownCount(PAGE_SIZE);
  }
  useEffect(() => {
    scrollContainer?.scrollTo({ top: 0 });
    // Only for a new question, not when the container first turns up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionKey]);

  const groups = useMemo(
    () =>
      merchants.slice(0, shownCount).map(
        (merchant): GroupedBarGroup<MerchantBarRow> => ({
          ...merchant,
          id: merchant.merchant.name,
          kind: "group",
          groupId: merchant.merchant.name,
          label: merchant.merchant.name,
          type,
          currency,
          categories: [],
        }),
      ),
    [merchants, shownCount, type, currency],
  );

  // The marker sits where the end of the list is headed, not where it is
  // while the chart grows to it, so a page is only added once the last one
  // is in place. It's remade with each page, and a fresh one always reports
  // whether it's in view, so a screen tall enough for more keeps filling.
  const { ref: endRef } = useInView({
    root: scrollContainer,
    rootMargin: `0px 0px ${LOAD_AHEAD_PX}px 0px`,
    skip: !scrollContainer || shownCount >= merchants.length,
    onChange: (inView) => {
      if (inView) setShownCount((count) => count + PAGE_SIZE);
    },
  });

  const getValue = useCallback(
    (row: MerchantBarRow) => (sort === "total" ? row.total : row.count),
    [sort],
  );
  const onSelectRow = useCallback(
    (row: MerchantBarRow) =>
      onSelect({
        id: row.id,
        label: row.label,
        transactions: row.transactions,
      }),
    [onSelect],
  );

  return (
    <div className={buildClasses("flex", "flex-col")}>
      <ColumnHeadings />
      <div className={buildClasses("relative")}>
        <GroupedBarChart<MerchantBarRow>
          groups={groups}
          expandedGroupIds={NO_GROUPS}
          onToggleGroup={noop}
          selectedId={selectedId}
          onSelectRow={onSelectRow}
          getValue={getValue}
          getColor={(row) => COLORS[row.type]}
          barContent={MerchantBar}
          tooltip={MerchantTooltip}
          labelIcon={MerchantLabelLogo}
          marginRight={MARGIN_RIGHT_PX}
          ariaLabel={`Merchants ranked by ${
            sort === "total" ? "total" : "number of transactions"
          }`}
        />
        <div
          key={shownCount}
          ref={endRef}
          aria-hidden={true}
          className={buildClasses("absolute", "inset-x-0", "h-px")}
          style={{
            top: `${groupedBarChartHeightPx(groups.length, {
              valueAxis: false,
            })}px`,
          }}
        />
      </div>
    </div>
  );
}

const NO_GROUPS: ReadonlySet<string> = new Set();
function noop() {}
